// Turns GET /farm-commander/status into a list of pass/warn/fail checks for
// the front-page Farm Commander panel. Pure so it can be unit tested.
//
// Every age is measured against the server's clock (payload.serverTime), not
// the browser's, so a laptop with a drifting clock can't fake a stale/healthy
// heartbeat. FarmCommander writes its heartbeat every 60 s
// (garageFarmServer FarmCommander/services/status.js).

export const HEARTBEAT_STALE_MS = 3 * 60_000;
export const COMMAND_STUCK_MS   = 10 * 60_000;
export const AC_STALE_MS        = 25 * 60_000;   // AC is polled every 10 min
export const ERROR_WINDOW_MS    = 60 * 60_000;

const RANK = { ok: 0, unknown: 1, warn: 2, bad: 3 };
export const worstTone = (tones) =>
  tones.reduce((w, t) => (RANK[t] > RANK[w] ? t : w), 'ok');

const ms = (d) => (d ? new Date(d).getTime() : NaN);

export function fmtAgo(ageMs) {
  if (!Number.isFinite(ageMs)) return '-';
  const s = Math.max(0, Math.round(ageMs / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m ago`;
  return `${Math.floor(h / 24)} days ago`;
}

export const fmtClock = (d) => {
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '-' : t.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

export function buildChecks(payload) {
  const now = ms(payload?.serverTime) || Date.now();
  const age = (d) => now - ms(d);
  const c = payload?.commander;
  const ctl = payload?.controller || {};
  const checks = [];

  // 1. Heartbeat - the master signal. If this is stale, everything the
  //    commander reported about itself is stale too.
  const hbAge = age(c?.heartbeatAt);
  const alive = Number.isFinite(hbAge) && hbAge < HEARTBEAT_STALE_MS;
  checks.push({
    key: 'heartbeat', label: 'Heartbeat',
    tone: alive ? 'ok' : 'bad',
    value: !c ? 'Never reported' : alive ? fmtAgo(hbAge) : `Silent since ${fmtClock(c.heartbeatAt)}`,
    detail: !c
      ? 'No heartbeat in the database. Is the new FarmCommander deployed?'
      : alive ? `Up since ${fmtClock(c.startedAt)} (pid ${c.pid})`
      : `Last heartbeat ${fmtAgo(hbAge)}. Process down, hung, or can't reach MongoDB. Check pm2 logs 1.`,
  });

  const stale = (tone) => (alive ? tone : 'unknown');

  // 2. Config - defaults means it booted without the DB config (2026-09-30).
  const src = c?.config?.source;
  checks.push({
    key: 'config', label: 'Config',
    tone: stale(src === 'db' ? 'ok' : src === 'defaults' ? 'bad' : 'unknown'),
    value: src === 'db' ? 'From database' : src === 'defaults' ? 'Built-in defaults' : '-',
    detail: src === 'defaults'
      ? 'Not using your saved schedule/AC/dosing settings. Restart Farm Commander.'
      : c?.config?.lastReloadAt ? `Last reloaded ${fmtClock(c.config.lastReloadAt)}` : null,
  });

  // 3. Scheduler tick
  const sch = c?.scheduler || {};
  const tickAge = age(sch.lastTickAt);
  const tickLimit = ((sch.intervalMinutes || 30) + 5) * 60_000;
  let tickTone = 'unknown';
  if (Number.isFinite(tickAge)) tickTone = tickAge > tickLimit ? 'bad' : sch.lastTickOk === false ? 'warn' : 'ok';
  checks.push({
    key: 'scheduler', label: 'Scheduler',
    tone: stale(tickTone),
    value: Number.isFinite(tickAge) ? `Ticked ${fmtAgo(tickAge)}` : 'No tick yet',
    detail: [
      sch.lastTickOk === false ? 'Last tick hit an error (see errors below).' : null,
      sch.nextTickAt ? `Next at ${fmtClock(sch.nextTickAt)}` : null,
    ].filter(Boolean).join(' ') || null,
  });

  // 4. Controller pickup - commands sitting in the queue mean the ESP
  //    isn't collecting them, regardless of how healthy the commander is.
  const pendAge = age(ctl.oldestPending?.createdAt);
  const stuck = ctl.pendingCount > 0 && pendAge > COMMAND_STUCK_MS;
  checks.push({
    key: 'controller', label: 'Controller',
    tone: stuck ? 'bad' : 'ok',
    value: !ctl.pendingCount ? 'Queue empty' : `${ctl.pendingCount} queued`,
    detail: stuck
      ? `Oldest waiting since ${fmtClock(ctl.oldestPending.createdAt)}. The controller isn't collecting commands.`
      : ctl.lastAck ? `Last ack ${fmtAgo(age(ctl.lastAck.at))}${ctl.lastAck.status !== 'ok' ? ` (${ctl.lastAck.status})` : ''}` : null,
  });

  // 5. AC unit
  const ac = c?.ac;
  const acAge = age(ac?.at);
  let acTone = 'unknown';
  if (ac) acTone = !ac.ok ? 'bad' : acAge > AC_STALE_MS ? 'warn' : 'ok';
  checks.push({
    key: 'ac', label: 'AC unit',
    tone: stale(acTone),
    value: !ac ? '-' : !ac.ok ? 'Not responding'
      : ac.power ? `${ac.mode} ${ac.setTemp}°C` : 'Off',
    detail: !ac ? 'No status read yet'
      : !ac.ok ? `${ac.error || 'No response'} (${fmtAgo(acAge)})`
      : `Room ${ac.currentTemp}°C · fan ${ac.fan} · read ${fmtAgo(acAge)}`,
  });

  // 6. Errors in the last hour
  const recent = (c?.errors || []).filter(e => age(e.lastAt || e.at) < ERROR_WINDOW_MS);
  const recentCount = recent.reduce((n, e) => n + (e.count || 1), 0);
  const hasHardError = recent.some(e => e.level === 'error');
  checks.push({
    key: 'errors', label: 'Errors (1h)',
    tone: stale(recentCount === 0 ? 'ok' : hasHardError ? 'bad' : 'warn'),
    value: recentCount === 0 ? 'None' : `${recentCount} logged`,
    detail: recent[0] ? recent[0].message : null,
  });

  return checks;
}

export function summarize(checks) {
  const tone = worstTone(checks.map(c => c.tone));
  const hb = checks.find(c => c.key === 'heartbeat');
  if (hb?.tone === 'bad') return { tone: 'bad', headline: 'Not reporting' };
  const failing = checks.filter(c => c.tone === 'bad' || c.tone === 'warn').map(c => c.label);
  if (!failing.length) return { tone: 'ok', headline: 'Running normally' };
  return { tone, headline: `Check ${failing.join(', ')}` };
}
